import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import Select, { components } from "react-select";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap";

import {
  fetchConversationMatrixByGroup,
  fetchConversationMatrixStages,
  fetchConversationMatrixTemplates,
  submitConversationMatrixBulk,
} from "model/ConversationMatrixModel";
import { getConversationMatrixGroupList } from "model/ConversationMatrixGroupModel";
import { usePermissionContext } from "libs/context/PermissionContext";
import { selectMenuStyles } from "pages/AllCommonModal/AIScheduleMatrixAddEditModal/ai-schedule-matrix-add-edit-modal";
import Spinners from "components/Common/Spinner";
import ConversationMatrixGroupAddEditModal from "pages/AllCommonModal/ConversationMatrixGroupAddEditModal/conversation-matrix-group-add-edit-modal";
import { toast } from "react-toastify";
import "pages/Call/ai-schedule-matrix.scss";
import "./conversation-matrix.scss";

const selectZIndexStyles = selectMenuStyles;

const SELECT_CONVERSATION_TEMPLATE_OPTION = {
  value: "select-conversation-option",
  label: "Select Conversation Template",
};

const isUnsetConversationTemplate = (templateId) => {
  const value = String(templateId ?? "").trim();
  return !value || value === SELECT_CONVERSATION_TEMPLATE_OPTION.value;
};

const mapConversationMatrixGroupOptions = (response) => {
  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.records)
      ? response.data.records
      : [];
  return list
    .map((item) => {
      const value = String(item?.id ?? item?.Id ?? "").trim();
      const label = String(
        item?.title ?? item?.display ?? item?.name ?? item?.label ?? value
      ).trim();
      if (!value) return null;
      return { value, label: label || value };
    })
    .filter(Boolean);
};

const mapMatrixTemplateOptions = (response) => {
  const list = Array.isArray(response?.data) ? response.data : [];
  return list
    .map((item) => {
      const value = item?.id ?? item?.Id;
      if (value == null || value === "") return null;
      const stringValue = String(value);
      if (stringValue === SELECT_CONVERSATION_TEMPLATE_OPTION.value) return null;
      const label = String(item?.label ?? item?.title ?? value).trim();
      return { value: stringValue, label: label || stringValue };
    })
    .filter(Boolean);
};

const extractDataArray = (response) => {
  const raw = response?.data;
  return Array.isArray(raw) ? raw : [];
};

const isFirstCallStage = (item) => {
  const stageId = Number(item?.stageId ?? item?.StageId);
  if (stageId === 1) return true;
  const name = String(item?.stageName ?? item?.StageName ?? "")
    .trim()
    .toLowerCase();
  return name === "first call";
};

const withoutFirstCallStage = (list = []) =>
  (Array.isArray(list) ? list : []).filter((item) => !isFirstCallStage(item));

const apiRowToStateRow = (row, fallbackRowIndex) => ({
  id: row?.id ?? null,
  rowIndex:
    row?.rowIndex === 0 || row?.rowIndex
      ? Number(row.rowIndex)
      : fallbackRowIndex,
  stageId: row?.stageId ?? row?.StageId ?? "",
  stageName: String(row?.stageName ?? row?.StageName ?? "").trim(),
  conversationTemplateId: row?.conversationTemplateId
    ? String(row.conversationTemplateId)
    : "",
  templateLabel: String(
    row?.templateLabel ?? row?.label ?? ""
  ).trim(),
});

const stageOnlyToStateRow = (stage, rowIndex) => ({
  id: null,
  rowIndex,
  stageId: stage?.stageId ?? stage?.StageId ?? "",
  stageName: String(stage?.stageName ?? stage?.StageName ?? "").trim(),
  conversationTemplateId: "",
  templateLabel: "",
});

const ConversationMatrix = () => {
  const { permissionData } = usePermissionContext() || {};

  const actionPermissions = useMemo(() => {
    const hasPageAccess = Boolean(
      permissionData?.ConversationMatrixAccess ||
        permissionData?.ConversationTemplateAccess
    );
    return {
      canCreate: Boolean(
        permissionData?.ConversationMatrixAddAccess ||
          permissionData?.ConversationTemplateAddAccess ||
          hasPageAccess
      ),
      canUpdate: Boolean(
        permissionData?.ConversationMatrixUpdateAccess ||
          permissionData?.ConversationTemplateUpdateAccess ||
          hasPageAccess
      ),
    };
  }, [permissionData]);

  const groupActionPermissions = useMemo(() => {
    const hasPageAccess = Boolean(
      permissionData?.ConversationMatrixAccess ||
        permissionData?.ConversationTemplateAccess
    );
    return {
      canCreate: Boolean(
        permissionData?.ConversationMatrixGroupAddAccess ||
          permissionData?.ConversationMatrixAddAccess ||
          hasPageAccess
      ),
      canEdit: Boolean(
        permissionData?.ConversationMatrixGroupUpdateAccess ||
          permissionData?.ConversationMatrixUpdateAccess ||
          hasPageAccess
      ),
    };
  }, [permissionData]);

  const [optionsLoading, setOptionsLoading] = useState(true);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [matrixIsNew, setMatrixIsNew] = useState(false);
  const [matrixLoaded, setMatrixLoaded] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupEditItemId, setGroupEditItemId] = useState(null);

  const showMatrixEditor = Boolean(selectedGroupId) && groupOptions.length > 0;

  const loadConversationMatrixGroupOptions = useCallback(async () => {
    const response = await getConversationMatrixGroupList();
    const nextOptions = mapConversationMatrixGroupOptions(response);
    setGroupOptions(nextOptions);
    return nextOptions;
  }, []);

  const openGroupCreateModal = useCallback(() => {
    setGroupEditItemId(null);
    setGroupModalOpen(true);
  }, []);

  const openGroupEditModal = useCallback((groupId) => {
    const resolvedId = String(groupId ?? "").trim();
    if (!resolvedId) return;
    setGroupEditItemId(resolvedId);
    setGroupModalOpen(true);
  }, []);

  const closeGroupModal = useCallback(() => {
    setGroupModalOpen(false);
    setGroupEditItemId(null);
  }, []);

  const handleGroupModalSuccess = useCallback(async () => {
    await loadConversationMatrixGroupOptions();
  }, [loadConversationMatrixGroupOptions]);

  const loadTemplateOptions = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await fetchConversationMatrixTemplates();
      setTemplateOptions(mapMatrixTemplateOptions(response));
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const loadMatrixForGroup = useCallback(async (groupId) => {
    const resolvedGroupId = String(groupId ?? "").trim();
    if (!resolvedGroupId) {
      setRows([]);
      setMatrixIsNew(false);
      setMatrixLoaded(false);
      return;
    }

    setLoading(true);
    setMatrixLoaded(false);
    try {
      const response = await fetchConversationMatrixByGroup(resolvedGroupId);
      const byGroupList = withoutFirstCallStage(extractDataArray(response));

      if (byGroupList.length > 0) {
        const sorted = [...byGroupList].sort(
          (a, b) => Number(a?.stageId ?? 0) - Number(b?.stageId ?? 0)
        );
        const nextRows = sorted.map((row, index) =>
          apiRowToStateRow(row, index)
        );
        setRows(nextRows);
        setMatrixIsNew(false);
      } else {
        const stagesResponse = await fetchConversationMatrixStages();
        const stages = withoutFirstCallStage(extractDataArray(stagesResponse)).sort(
          (a, b) => Number(a?.stageId ?? 0) - Number(b?.stageId ?? 0)
        );
        setRows(stages.map((stage, index) => stageOnlyToStateRow(stage, index)));
        setMatrixIsNew(true);
      }

      setMatrixLoaded(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    const init = async () => {
      setOptionsLoading(true);
      try {
        await Promise.all([
          loadTemplateOptions(),
          loadConversationMatrixGroupOptions(),
        ]);
      } finally {
        if (active) setOptionsLoading(false);
      }
    };
    init();
    return () => {
      active = false;
    };
  }, [loadConversationMatrixGroupOptions, loadTemplateOptions]);

  const handleGroupChange = (selectedOption) => {
    const nextGroupId = selectedOption?.value ? String(selectedOption.value) : "";
    setSelectedGroupId(nextGroupId);
    setMatrixLoaded(false);
    if (!nextGroupId) {
      setRows([]);
      setMatrixIsNew(false);
      return;
    }
    setRows([]);
    loadMatrixForGroup(nextGroupId);
  };

  const getSelectedGroupOption = () =>
    groupOptions.find(
      (option) => String(option?.value) === String(selectedGroupId)
    ) || null;

  const getTemplateSelectOptions = (row) => {
    let options = templateOptions;
    if (
      row?.conversationTemplateId &&
      row?.templateLabel &&
      !templateOptions.some(
        (opt) => String(opt.value) === String(row.conversationTemplateId)
      ) &&
      !isUnsetConversationTemplate(row.conversationTemplateId)
    ) {
      options = [
        { value: row.conversationTemplateId, label: row.templateLabel },
        ...templateOptions,
      ];
    }
    return [SELECT_CONVERSATION_TEMPLATE_OPTION, ...options];
  };

  const updateRowTemplate = (rowIndex, selectedOption) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === rowIndex
          ? {
              ...row,
              conversationTemplateId: isUnsetConversationTemplate(
                selectedOption?.value
              )
                ? ""
                : String(selectedOption.value),
              templateLabel: isUnsetConversationTemplate(selectedOption?.value)
                ? ""
                : (selectedOption?.label ?? ""),
            }
          : row
      )
    );
  };

  const buildSavePayload = (mode) =>
    rows.map((row, index) => {
      const templateId = String(row.conversationTemplateId ?? "").trim();
      const payload = {
        rowIndex:
          row.rowIndex === 0 || row.rowIndex ? Number(row.rowIndex) : index,
        stageId: Number(row.stageId),
        groupId: String(selectedGroupId).trim(),
        conversationTemplateId: isUnsetConversationTemplate(templateId)
          ? null
          : templateId,
      };
      if (mode === "update") {
        const rowId = String(row?.id ?? "").trim();
        if (rowId) {
          payload.id = rowId;
        }
      }
      return payload;
    });

  const handleSave = async (mode) => {
    if (!selectedGroupId) {
      toast.error("Select a conversation matrix group first");
      return;
    }
    if (mode === "create" && !actionPermissions.canCreate) return;
    if (mode === "update" && !actionPermissions.canUpdate) return;
    if (mode === "create" && !matrixIsNew) {
      toast.error("This group already has a conversation matrix. Use Update.");
      return;
    }
    if (mode === "update" && matrixIsNew) {
      toast.error("Create the conversation matrix first.");
      return;
    }

    setSaving(true);
    try {
      const response = await submitConversationMatrixBulk(
        buildSavePayload(mode)
      );
      if (!response || response?.success === false) {
        toast.error(response?.message || "Unable to save conversation matrix");
        return;
      }
      toast.success(
        response?.message ||
          (mode === "create"
            ? "Conversation matrix created successfully"
            : "Conversation matrix updated successfully")
      );
      await loadMatrixForGroup(selectedGroupId);
    } finally {
      setSaving(false);
    }
  };

  const renderSelectPortalProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: selectZIndexStyles,
  };

  const conversationMatrixGroupSelectStyles = useMemo(
    () => ({
      ...selectZIndexStyles,
      menu: (base) => ({
        ...(typeof selectZIndexStyles.menu === "function"
          ? selectZIndexStyles.menu(base)
          : base),
        padding: 0,
        overflow: "hidden",
      }),
      option: (base) => ({
        ...base,
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 0.5rem 0 0.75rem",
      }),
    }),
    []
  );

  const conversationMatrixGroupSelectComponents = useMemo(() => {
    const { canEdit, canCreate } = groupActionPermissions;
    if (!canEdit && !canCreate) {
      return undefined;
    }

    const customComponents = {};

    if (canEdit) {
      function ConversationMatrixGroupSelectOption(optionProps) {
        const groupId = optionProps?.data?.value;
        return (
          <components.Option {...optionProps}>
            <div className="schedule-matrix-group-option">
              <span className="schedule-matrix-group-option__label text-truncate">
                {optionProps.label}
              </span>
              <button
                type="button"
                className="schedule-matrix-group-option__edit"
                title="Edit group"
                aria-label={`Edit ${optionProps.label}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openGroupEditModal(groupId);
                }}
              >
                <i className="mdi mdi-pencil-outline" aria-hidden="true" />
              </button>
            </div>
          </components.Option>
        );
      }
      customComponents.Option = ConversationMatrixGroupSelectOption;
    }

    if (canCreate) {
      function ConversationMatrixGroupSelectMenuList(menuListProps) {
        return (
          <div className="schedule-matrix-group-menu">
            <components.MenuList {...menuListProps}>
              {menuListProps.children}
            </components.MenuList>
            <div
              className="schedule-matrix-group-menu-footer"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <button
                type="button"
                className="schedule-matrix-group-menu-footer__add"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openGroupCreateModal();
                }}
              >
                <i className="fa fa-plus" aria-hidden="true" />
                <span>Add group</span>
              </button>
            </div>
          </div>
        );
      }
      customComponents.MenuList = ConversationMatrixGroupSelectMenuList;
    }

    return customComponents;
  }, [
    groupActionPermissions.canEdit,
    groupActionPermissions.canCreate,
    openGroupEditModal,
    openGroupCreateModal,
  ]);

  const showTable =
    Boolean(selectedGroupId) && !loading && rows.length > 0;

  return (
    <React.Fragment>
      <div className="page-content ai-schedule-matrix-page conversation-matrix-page">
        <Container fluid>
          <Card className="shadow-sm border-0">
            <CardBody
              className={
                showMatrixEditor && showTable ? "" : "card-body-90-vh"
              }
            >
              <h5 className="fw-bold mb-0 pe-2">Conversation Matrix</h5>
              <p className="text-muted small mb-0 mt-1">
                Choose a group, then assign conversation templates to each stage.
              </p>
              <hr />

              <div className="schedule-matrix-toolbar schedule-matrix-toolbar--group-centered">
                <Row className="align-items-end g-3 justify-content-center">
                  <Col xs={12} lg={6}>
                    <div className="schedule-matrix-group-field">
                      <div className="toolbar-label text-center">
                        Conversation Matrix Group
                      </div>
                      {optionsLoading ? (
                        <Spinners setLoading={setOptionsLoading} />
                      ) : groupOptions.length ||
                        groupActionPermissions.canCreate ? (
                        <Select
                          options={groupOptions}
                          className="select2-selection schedule-matrix-group-select"
                          classNamePrefix="schedule-matrix-group-rs"
                          placeholder="Select conversation matrix group"
                          value={getSelectedGroupOption()}
                          onChange={handleGroupChange}
                          isDisabled={loading || saving}
                          components={conversationMatrixGroupSelectComponents}
                          noOptionsMessage={() => "No groups found"}
                          styles={conversationMatrixGroupSelectStyles}
                          menuPortalTarget={
                            typeof document !== "undefined"
                              ? document.body
                              : null
                          }
                          menuPosition="fixed"
                        />
                      ) : (
                        <p className="text-muted mb-0 small text-center">
                          No conversation matrix groups found.
                        </p>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {loading ? (
                <div className="mt-3">
                  <Spinners setLoading={setLoading} />
                </div>
              ) : null}

              {showTable ? (
                <div className="schedule-matrix-table-wrap">
                  <table className="table schedule-matrix-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th scope="col" className="conversation-matrix-th-stage">
                          Stage
                        </th>
                        <th scope="col">
                          Conversation template{" "}
                          <span className="text-muted fw-normal">(optional)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rowIndex) => {
                        const options = getTemplateSelectOptions(row);
                        const templateValue = isUnsetConversationTemplate(
                          row.conversationTemplateId
                        )
                          ? SELECT_CONVERSATION_TEMPLATE_OPTION
                          : options.find(
                              (opt) =>
                                String(opt.value) ===
                                String(row.conversationTemplateId ?? "")
                            ) ||
                            (row.conversationTemplateId && row.templateLabel
                              ? {
                                  value: row.conversationTemplateId,
                                  label: row.templateLabel,
                                }
                              : SELECT_CONVERSATION_TEMPLATE_OPTION);
                        return (
                          <tr
                            key={row.id ?? `stage-${row.stageId}-${rowIndex}`}
                          >
                            <td data-label="Stage">
                              <span className="fw-semibold">
                                {row.stageName ||
                                  `Stage ${row.stageId ?? rowIndex + 1}`}
                              </span>
                              {/* {row.stageId !== "" && row.stageId != null ? (
                                <span className="text-muted small ms-2">
                                  (#{row.stageId})
                                </span>
                              ) : null} */}
                            </td>
                            <td data-label="Conversation template">
                              <div className="schedule-matrix-cell">
                                <Select
                                  options={options}
                                  className="select2-selection schedule-matrix-select"
                                  placeholder={
                                    loadingTemplates
                                      ? "Loading..."
                                      : "Choose conversation template"
                                  }
                                  isLoading={loadingTemplates}
                                  value={templateValue}
                                  onChange={(opt) =>
                                    updateRowTemplate(rowIndex, opt)
                                  }
                                  {...renderSelectPortalProps}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {(!selectedGroupId ||
                (!loading && selectedGroupId && !rows.length)) && (
                <div className="schedule-matrix-empty">
                  <div className="empty-icon">
                    <i className="fa fa-table" aria-hidden="true" />
                  </div>
                  <h6 className="fw-semibold text-secondary mb-1">
                    {!selectedGroupId
                      ? "Select a conversation matrix group"
                      : "No stages for this group"}
                  </h6>
                  <p className="small mb-0 mx-auto" style={{ maxWidth: "360px" }}>
                    {!selectedGroupId
                      ? "Pick a group above to load stage templates."
                      : "There are no matrix rows yet for this group."}
                  </p>
                </div>
              )}

              {showTable &&
              ((matrixIsNew && actionPermissions.canCreate) ||
                (!matrixIsNew && actionPermissions.canUpdate)) ? (
                <div className="conversation-matrix-actions">
                  {matrixIsNew && actionPermissions.canCreate ? (
                    <Button
                      color="primary"
                      type="button"
                      className="schedule-matrix-section-btn px-4"
                      disabled={saving || !matrixLoaded}
                      onClick={() => handleSave("create")}
                    >
                      {saving ? "Saving..." : "Create"}
                    </Button>
                  ) : null}
                  {!matrixIsNew && actionPermissions.canUpdate ? (
                    <Button
                      color="primary"
                      type="button"
                      className="schedule-matrix-section-btn px-4"
                      disabled={saving || !matrixLoaded}
                      onClick={() => handleSave("update")}
                    >
                      {saving ? "Saving..." : "Update"}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </CardBody>
          </Card>

          <ConversationMatrixGroupAddEditModal
            isOpen={groupModalOpen}
            onClose={closeGroupModal}
            itemId={groupEditItemId}
            onSuccess={handleGroupModalSuccess}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

ConversationMatrix.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ConversationMatrix);
